import { ref, computed, watch } from 'vue';
import { MdTypography, MdButton, MdIconAdd, MdIconSearch, MdModal, MdLink } from '@antiqua-dls/vue-components';
import filesErrorPopup from 'src/components/general/common-popup';
import agGrid from 'components/agGrid/index.vue';
import { disableScrollOnModalOpen } from 'src/utils/helper/misc';
import { SORTING_ORDER, SORTING_ORDER_DIRECTION, DOWNLOAD_FORMAT } from 'src/utils/constants/common';

export default {
    name: 'ErrorFilesPopup',
    components: {
        MdTypography,
        MdButton,
        MdIconSearch,
        MdIconAdd,
        MdModal,
        filesErrorPopup,
        agGrid,
        MdLink
    },

    props: {
        invalidFiles: {
            type: Array,
            default: () => []
        },
        modalTitle: {
            type: String,
            default: ''
        },
        modalSubTitle: {
            type: String,
            default: ''
        },
        firstColumnHeading: {
            type: String,
            default: ''
        },
        secondColumnHeading: {
            type: String,
            default: ''
        },
        moduleName: {
            type: String,
            default: ''
        },
        projectName: {
            type: String,
            default: ''
        },
        showPopup: {
            type: Boolean,
            default: false
        }
    },

    emits: ['update:showPopup', 'emit:adobeCjaEvent'],

    setup(props, { emit }) {
        const filesWithError = computed(() => props.invalidFiles);

        const rowData = computed(() => {
            return filesWithError.value.map(item => ({
                fileName: item.name,
                reasonForRejection: item.error
            }));
        });

        const colDefs = computed(() => [
            {
                field: 'fileName',
                resizable: false,
                autoHeight: true,
                wrapText: true,
                flex: 1,
                cellClass: 'file-name-error-cell-class',
                sortable: false,
                headerName: props.firstColumnHeading
            },
            {
                field: 'reasonForRejection',
                resizable: false,
                headerName: props.secondColumnHeading,
                flex: 1,
                autoHeight: true,
                sortable: true,
                cellClass: 'error-reason-cell-class',
                sort: SORTING_ORDER_DIRECTION.asc,
                sortingOrder: SORTING_ORDER,
                // i18n messages may include safe HTML (e.g. mailto links)
                cellRenderer: (params) => {
                    const div = document.createElement('div');
                    div.innerHTML = params.value || '';
                    return div;
                },
                icons: {
                    sortAscending:
                        '<md-icon-sort rotate="down" size="small" icontitle="enabled" color="spirits--600"></md-icon-sort>',
                    sortDescending:
                        '<md-icon-sort rotate="up" size="small" icontitle="enabled" color="spirits--600"></md-icon-sort>'
                }
            }
        ]);

        const showDownloadList = ref(false);

        const handleClose = () => {
            emit('update:showPopup', false);
        };

        const downloadCSVorExcel = format => {
            const delimiter = format === DOWNLOAD_FORMAT.csv ? ',' : '\t';
            const mimeType = format === DOWNLOAD_FORMAT.csv ? DOWNLOAD_FORMAT.mimeType_csv : DOWNLOAD_FORMAT.mimeType_xls;
            const extension = format === DOWNLOAD_FORMAT.csv ? DOWNLOAD_FORMAT.csv : DOWNLOAD_FORMAT.xls;

            const data = filesWithError.value;
            const header = Object.keys(data[0]).join(delimiter) + '\n';
            // Strip HTML tags so exports stay readable (e.g. combo_sku_issue mailto link)
            const stripHtml = (value) =>
                typeof value === 'string' ? value.replace(/<[^>]*>/g, '') : value;
            const rows = data
                .map(row => Object.values(row).map(stripHtml).join(delimiter))
                .join('\n');
            const content = header + rows;

            const blob = new Blob([content], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date()
                .toLocaleString('en-GB', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                })
                .replace(/[/, ]/g, '_')
                .replace(/:/g, '.');
            link.setAttribute('download', `${props.projectName}_${timestamp}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            showDownloadList.value = false;
            emit('emit:adobeCjaEvent');
        };

        watch(
            () => props.showPopup,
            () => {
                disableScrollOnModalOpen(props.showPopup);
            }
        );

        return {
            filesWithError,
            showDownloadList,
            colDefs,
            rowData,
            downloadCSVorExcel,
            handleClose
        };
    }
};
